/** Pipeline steps written by functions/websiteAudit.js — keep ids in sync. */
export const WEBSITE_AUDIT_PIPELINE_STEPS = [
  { id: 'auth_verified', label: 'Verify authentication', fn: 'createWebsiteAudit' },
  { id: 'resolving_account', label: 'Resolve account ID', fn: 'createWebsiteAudit' },
  { id: 'dataforseo_task_post', label: 'POST /on_page/task_post (start crawl)', fn: 'createWebsiteAudit' },
  { id: 'dataforseo_domain_metrics', label: 'Fetch domain metrics (Labs + backlinks)', fn: 'createWebsiteAudit' },
  { id: 'firestore_pending', label: 'Save pending audit to Firestore', fn: 'createWebsiteAudit' },
  { id: 'awaiting_crawl', label: 'Wait for DataForSEO crawl', fn: 'processPendingWebsiteAudits' },
  { id: 'checking_crawl_status', label: 'Check crawl ready (tasks_ready / summary)', fn: 'processPendingWebsiteAudits' },
  { id: 'fetching_results', label: 'Fetch summary and pages (DataForSEO)', fn: 'processPendingWebsiteAudits' },
  { id: 'building_dashboard', label: 'Build dashboard metrics', fn: 'processPendingWebsiteAudits' },
  { id: 'saving_results', label: 'Save to accounts/{accountId}/audits', fn: 'processPendingWebsiteAudits' },
  { id: 'completed', label: 'Audit complete', fn: 'processPendingWebsiteAudits' },
];

export function getPipelineStepIndex(stepId) {
  if (!stepId) return -1;
  return WEBSITE_AUDIT_PIPELINE_STEPS.findIndex((s) => s.id === stepId);
}

export function getStepStatus(stepId, currentStepId, auditStatus) {
  if (auditStatus === 'failed') {
    const failedIdx = getPipelineStepIndex(currentStepId);
    const idx = getPipelineStepIndex(stepId);
    if (idx < failedIdx) return 'done';
    if (idx === failedIdx) return 'error';
    return 'pending';
  }
  if (auditStatus === 'completed' || currentStepId === 'completed') {
    return 'done';
  }
  const currentIdx = getPipelineStepIndex(currentStepId);
  const idx = getPipelineStepIndex(stepId);
  if (idx < 0 || currentIdx < 0) return 'pending';
  if (idx < currentIdx) return 'done';
  if (idx === currentIdx) return 'active';
  return 'pending';
}

/** Partial card values from domainMetrics on a pending websiteAudits doc. */
export function metricsFromDomainMetrics(domainMetrics) {
  if (!domainMetrics) return null;
  const organic = domainMetrics.organic || {};
  const ranked = domainMetrics.rankedKeywords || {};
  const backlinks = domainMetrics.backlinks || {};
  return {
    seoScore: null,
    onPageScore: null,
    organicTraffic: {
      amount: organic.etv != null ? Math.round(organic.etv) : null,
      rankingKeywords: organic.count ?? ranked.total ?? null,
    },
    rankedKeywords: {
      total: ranked.total ?? organic.count ?? null,
      top10: ranked.top10 ?? null,
    },
    backlinkProfile: {
      backlinks: backlinks.backlinks ?? null,
    },
    pageAudit: { loadSpeed: null },
    errorsWarnings: { criticalErrors: null },
  };
}

export const EMPTY_AUDIT_METRICS = {
  seoScore: null,
  onPageScore: null,
  organicTraffic: { amount: null, rankingKeywords: null },
  rankedKeywords: { total: null, top10: null },
  backlinkProfile: { backlinks: null },
  pageAudit: { loadSpeed: null },
  errorsWarnings: { criticalErrors: null },
};

export function formatMetricValue(value, { suffix = '', locale = true } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number' && locale) return value.toLocaleString() + suffix;
  return String(value) + suffix;
}

export function formatLoadSpeed(loadSpeed) {
  if (loadSpeed === null || loadSpeed === undefined || loadSpeed === '') return '—';
  return `${loadSpeed}s`;
}

/** True when on-page crawl metrics are present (not just domain/Labs data). */
export function hasOnPageMetrics(data) {
  if (!data) return false;
  return (
    data.seoScore != null
    || data.onPageScore != null
    || data.pageAudit?.loadSpeed != null
    || data.errorsWarnings?.criticalErrors != null
  );
}

/** Prefer completed on-page values; keep domain metrics where on-page is still missing. */
export function mergeAuditMetrics(completed, partial) {
  if (!completed) return partial || EMPTY_AUDIT_METRICS;
  if (!partial) return completed;
  return {
    ...partial,
    ...completed,
    seoScore: completed.seoScore ?? partial.seoScore,
    onPageScore: completed.onPageScore ?? partial.onPageScore,
    organicTraffic: { ...partial.organicTraffic, ...completed.organicTraffic },
    rankedKeywords: { ...partial.rankedKeywords, ...completed.rankedKeywords },
    backlinkProfile: { ...partial.backlinkProfile, ...completed.backlinkProfile },
    pageAudit: { ...partial.pageAudit, ...completed.pageAudit },
    errorsWarnings: { ...partial.errorsWarnings, ...completed.errorsWarnings },
  };
}

/** Pick transform input from a Firestore audit or websiteAudits document. */
export function auditDocToTransformSource(doc) {
  if (!doc) return null;
  if (doc.organicTraffic && doc.backlinkProfile) return doc;
  if (doc.results?.organicTraffic) return doc.results;
  return doc.results || doc;
}
